import { useState, useEffect, useCallback, useRef } from 'react';
import { getSales, getCartMetrics, calculateMetrics, deleteSale as deleteSaleAPI, updateSaleStatus as updateSaleStatusAPI, cancelSale as cancelSaleAPI } from '../../../services/sales-api';
import { handleValidationErrors } from '../../../utils/errorHandlers';
import { toast } from 'react-toastify';

export const useSalesData = (filters = {}) => {
  const [sales, setSales] = useState([]);
  const [creators, setCreators] = useState([]);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
    totalPages: 0
  });
  const [metrics, setMetrics] = useState({
    totalSales: 0,
    totalAmount: 0,
    netProfit: 0,
    unpaidCustomers: 0,
    salesChange: 0,
    amountChange: 0,
    profitChange: 0,
    unpaidChange: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isLoadingRef = useRef(false);
  const prevFiltersRef = useRef(null);

  // Load sales data from API
  const loadSalesData = useCallback(async (forceRefresh = false) => {
    // Prevent concurrent calls
    if (isLoadingRef.current) {
      return;
    }

    // Check if filters actually changed (unless forced refresh)
    if (!forceRefresh) {
      const filtersString = JSON.stringify(filters);
      const prevFiltersString = prevFiltersRef.current ? JSON.stringify(prevFiltersRef.current) : '';
      
      if (prevFiltersString === filtersString && prevFiltersRef.current !== null) {
        // Filters haven't changed, skip API call
        return;
      }
    }

    try {
      isLoadingRef.current = true;
      prevFiltersRef.current = filters;
      setLoading(true);
      setError(null);
      
      // Fetch sales data and metrics in parallel
      const [salesResponse, metricsData] = await Promise.all([
        getSales(filters),
        getCartMetrics(filters).catch(err => {
          
          return null;
        })
      ]);
      
      // Extract sales array and pagination from response
      const salesData = salesResponse.results || salesResponse.data || salesResponse;
      const pageSize = filters.page_size || 25;
      const totalPages = Math.ceil((salesResponse.count || 0) / pageSize);
      
      // Update pagination state
      setPagination({
        count: salesResponse.count || 0,
        next: salesResponse.next,
        previous: salesResponse.previous,
        currentPage: filters.page || 1,
        totalPages: totalPages
      });
      
      // Transform API data to match component expectations
      const transformedSales = salesData.map(sale => {
        // Parse the date and extract components to avoid timezone conversion
        const date = new Date(sale.created_at);
        const year = date.getUTCFullYear();
        const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
        const day = date.getUTCDate();
        const hours = date.getUTCHours();
        const minutes = date.getUTCMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        
        return {
        id: sale.id,
        invoiceId: sale.invoice_id,
        customerName: sale.customer_name,
        dateTime: `${month} ${day}, ${year}, ${displayHours.toString().padStart(2, '0')}:${minutes} ${ampm}`,
        amount: parseFloat(sale.total_amount) || 0,
        profit: parseFloat(sale.net_profit) || 0,
        creator: sale.creator_name || `User ${sale.created_by}`,
        status: (sale.status_display || sale.status)?.toLowerCase(),
        previousBalance: parseFloat(sale.previous_balance) || 0,
        totalQty: sale.total_qty || 0,
        products: sale.cart_items?.map(item => ({
          name: item.product_detail?.name || 'Unknown Product',
          brand: item.product_detail?.brand_name || '',
          buyPrice: 0, // Not available in API response
          sellPrice: parseFloat(item.price) || 0,
          quantity: item.qty || 0,
          image: null // Not available in API response
        })) || []
      };
      });
      
      setSales(transformedSales);
      
      // Extract unique creators from sales data
      const uniqueCreators = salesData.reduce((acc, sale) => {
        const creatorId = sale.created_by;
        if (!acc.find(c => c.id === creatorId)) {
          acc.push({
            id: creatorId,
            name: sale.creator_name || `User ${creatorId}`
          });
        }
        return acc;
      }, []);
      setCreators(uniqueCreators);
      
      // Use backend metrics if available, otherwise calculate locally
      const finalMetrics = metricsData || calculateMetrics(salesData);
      setMetrics(finalMetrics);
      
    } catch (err) {
      console.error('Error loading sales data:', err);
      setError(err.message || 'Failed to load sales data');
      handleValidationErrors(err, 'Failed to load sales data');
      
      // Set empty data on error
      setSales([]);
      setMetrics({
        totalSales: 0,
        totalAmount: 0,
        netProfit: 0,
        unpaidCustomers: 0,
        salesChange: 0,
        amountChange: 0,
        profitChange: 0,
        unpaidChange: 0
      });
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [filters]);

  useEffect(() => {
    const fetchData = async () => {
      await loadSalesData();
    };
    
    fetchData();
    
  }, [loadSalesData]);

  const deleteSale = async (saleId) => {
    try {
      await deleteSaleAPI(saleId);
      
      // Remove from local state
      const updatedSales = sales.filter(sale => sale.id !== saleId);
      setSales(updatedSales);
      
      toast.success('Sale deleted successfully');
      
      // Reload data to get updated metrics (force refresh)
      loadSalesData(true);
    } catch (err) {
      console.error('Error deleting sale:', err);
      handleValidationErrors(err, 'Failed to delete sale');
    }
  };

  const updateSaleStatus = async (saleId, newStatus) => {
    try {
      await updateSaleStatusAPI(saleId, newStatus);
      
      // Update local state
      const updatedSales = sales.map(sale => 
        sale.id === saleId ? { ...sale, status: newStatus } : sale
      );
      setSales(updatedSales);
      
      toast.success('Sale status updated successfully');
      
      // Reload data to get updated metrics (force refresh)
      loadSalesData(true);
    } catch (err) {
      console.error('Error updating sale status:', err);
      handleValidationErrors(err, 'Failed to update sale status');
    }
  };

  const cancelSale = async (saleId, reason) => {
    try {
      await cancelSaleAPI(saleId);
      
      // Update local state
      const updatedSales = sales.map(sale => 
        sale.id === saleId ? { ...sale, status: 'cancelled' } : sale
      );
      setSales(updatedSales);
      
      toast.success('Sale cancelled successfully');
      
      // Reload data to get updated metrics (force refresh)
      loadSalesData(true);
    } catch (err) {
      console.error('Error cancelling sale:', err);
      handleValidationErrors(err, 'Failed to cancel sale');
    }
  };

  return {
    sales,
    creators,
    pagination,
    metrics,
    loading,
    error,
    deleteSale,
    updateSaleStatus,
    cancelSale,
    refreshData: () => loadSalesData(true)
  };
};